import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';

import { AppState } from '@cpd-app/state';
import {
  selectLoadFailed,
  selectLoading,
  selectUsers,
  selectVerificationToken,
  selectVerifiedEmail
} from './user.selectors';

import {
  changePassword,
  changePasswordSuccess,
  createAccount,
  createAccountSuccess,
  deleteUser,
  getEmailToVerify,
  getUsers,
  inviteUsers,
  inviteUsersFailure,
  inviteUsersSuccess,
  resetPassword,
  resetPasswordFailure,
  resetPasswordSuccess,
  saveUser,
  verifyEmail,
  verifyEmailSuccess
} from './user.actions';
import {
  InviteUsersMutationVariables,
  SaveUserDetailsMutationVariables,
  VerifyEmailMutationVariables
} from '@cpd-app/generated';
import { CreateAccountInput } from '@cpd-app/core-module/models';

@Injectable({
  providedIn: 'root'
})
export class UserFacade {
  public readonly users$ = this.store.pipe(select(selectUsers));
  public readonly verifiedEmail$ = this.store.pipe(select(selectVerifiedEmail));
  public readonly verificationToken$ = this.store.pipe(
    select(selectVerificationToken)
  );
  public readonly loading$ = this.store.pipe(select(selectLoading));
  /** ANALYTICS.22. True when the last member load failed. */
  public readonly loadFailed$ = this.store.pipe(select(selectLoadFailed));
  public readonly verifyEmailResponse$ = this.actions$.pipe(
    ofType(verifyEmailSuccess)
  );
  public readonly inviteUsersResponse$ = this.actions$.pipe(
    ofType(inviteUsersSuccess, inviteUsersFailure)
  );

  public readonly createAccountResponse$ = this.actions$.pipe(
    ofType(createAccountSuccess)
  );

  public readonly resetPasswordResponse$ = this.actions$.pipe(
    ofType(resetPasswordSuccess, resetPasswordFailure)
  );

  public readonly changePasswordResponse$ = this.actions$.pipe(
    ofType(changePasswordSuccess)
  );

  constructor(
    private store: Store<AppState>,
    private actions$: Actions
  ) {}

  public getUsers(): void {
    this.store.dispatch(getUsers());
  }

  public getEmailToVerify(token: string): void {
    this.store.dispatch(getEmailToVerify({ token }));
  }

  public saveUserDetails(input: SaveUserDetailsMutationVariables): void {
    this.store.dispatch(saveUser({ input }));
  }

  public deleteUser(userId: number): void {
    this.store.dispatch(deleteUser({ userId }));
  }

  public inviteUsers(input: InviteUsersMutationVariables): void {
    this.store.dispatch(inviteUsers({ input }));
  }

  public verifyEmail(input: VerifyEmailMutationVariables): void {
    this.store.dispatch(verifyEmail({ input }));
  }

  public resetPassword(email: string): void {
    this.store.dispatch(resetPassword({ email }));
  }

  public changePassword(token: string, password: string): void {
    this.store.dispatch(changePassword({ token, password }));
  }

  public createAccount(input: CreateAccountInput): void {
    this.store.dispatch(createAccount({ input }));
  }
}
